// ---------------------------------------------------------------------------
// Styles – all CSS for the PatchPilot SDK widget, injected into Shadow DOM
// ---------------------------------------------------------------------------

export const cssVariablesLight = `
  --pp-bg: #ffffff;
  --pp-bg-secondary: #f8f9fa;
  --pp-bg-hover: #f1f3f5;
  --pp-text: #1a1a2e;
  --pp-text-secondary: #6b7280;
  --pp-text-placeholder: #9ca3af;
  --pp-border: #e5e7eb;
  --pp-border-focus: #6366f1;
  --pp-primary: #6366f1;
  --pp-primary-hover: #4f46e5;
  --pp-primary-text: #ffffff;
  --pp-danger: #ef4444;
  --pp-danger-hover: #dc2626;
  --pp-success: #10b981;
  --pp-success-bg: #ecfdf5;
  --pp-success-text: #065f46;
  --pp-error-bg: #fef2f2;
  --pp-error-text: #991b1b;
  --pp-overlay: rgba(0, 0, 0, 0.3);
  --pp-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  --pp-shadow-btn: 0 4px 14px rgba(99, 102, 241, 0.4);
  --pp-radius: 12px;
  --pp-radius-sm: 8px;
`;

export const cssVariablesDark = `
  --pp-bg: #1e1e2e;
  --pp-bg-secondary: #2a2a3c;
  --pp-bg-hover: #33334a;
  --pp-text: #e4e4f0;
  --pp-text-secondary: #9ca3af;
  --pp-text-placeholder: #6b7280;
  --pp-border: #3f3f5a;
  --pp-border-focus: #818cf8;
  --pp-primary: #818cf8;
  --pp-primary-hover: #6366f1;
  --pp-primary-text: #ffffff;
  --pp-danger: #f87171;
  --pp-danger-hover: #ef4444;
  --pp-success: #34d399;
  --pp-success-bg: #064e3b;
  --pp-success-text: #a7f3d0;
  --pp-error-bg: #450a0a;
  --pp-error-text: #fca5a5;
  --pp-overlay: rgba(0, 0, 0, 0.5);
  --pp-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
  --pp-shadow-btn: 0 4px 14px rgba(129, 140, 248, 0.35);
  --pp-radius: 12px;
  --pp-radius-sm: 8px;
`;

export const styles = `
  /* ---------- Theme variables ---------- */
  :host, :host([data-theme="light"]) {
    ${cssVariablesLight}
  }
  :host([data-theme="dark"]) {
    ${cssVariablesDark}
  }
  @media (prefers-color-scheme: dark) {
    :host([data-theme="auto"]) {
      ${cssVariablesDark}
    }
  }
  @media (prefers-color-scheme: light) {
    :host([data-theme="auto"]) {
      ${cssVariablesLight}
    }
  }

  /* ---------- Reset ---------- */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* ---------- Animations ---------- */
  @keyframes pp-scale-in {
    from { transform: scale(0); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }
  @keyframes pp-pulse {
    0%, 100% { box-shadow: var(--pp-shadow-btn); }
    50%      { box-shadow: 0 4px 24px rgba(99, 102, 241, 0.6); }
  }
  @keyframes pp-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes pp-slide-up {
    from { transform: translateY(20px); opacity: 0; }
    to   { transform: translateY(0); opacity: 1; }
  }
  @keyframes pp-spin {
    to { transform: rotate(360deg); }
  }

  /* ---------- Floating button ---------- */
  .pp-floating-btn {
    position: fixed;
    bottom: 24px;
    z-index: 9999;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: var(--pp-primary);
    color: var(--pp-primary-text);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--pp-shadow-btn);
    transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
    animation: pp-scale-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
               pp-pulse 2s ease-in-out 0.5s 3;
    outline: none;
  }
  .pp-floating-btn:hover {
    background: var(--pp-primary-hover);
    transform: scale(1.08);
  }
  .pp-floating-btn:active {
    transform: scale(0.95);
  }
  .pp-floating-btn:focus-visible {
    outline: 2px solid var(--pp-primary);
    outline-offset: 3px;
  }
  .pp-floating-btn--right {
    right: 24px;
  }
  .pp-floating-btn--left {
    left: 24px;
  }
  .pp-floating-btn svg {
    width: 26px;
    height: 26px;
    fill: currentColor;
  }

  /* ---------- Overlay ---------- */
  .pp-form-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: var(--pp-overlay);
    display: none;
    align-items: flex-end;
    justify-content: flex-end;
    padding: 24px;
    animation: pp-fade-in 0.2s ease forwards;
  }
  .pp-form-overlay--visible {
    display: flex;
  }
  .pp-form-overlay--left {
    justify-content: flex-start;
  }

  /* ---------- Form container ---------- */
  .pp-form-container {
    width: 100%;
    max-width: 420px;
    max-height: calc(100vh - 48px);
    background: var(--pp-bg);
    border-radius: var(--pp-radius);
    box-shadow: var(--pp-shadow);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: pp-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 14px;
    color: var(--pp-text);
    line-height: 1.5;
  }

  /* ---------- Header ---------- */
  .pp-form-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--pp-border);
    flex-shrink: 0;
  }
  .pp-form-header__title {
    font-size: 16px;
    font-weight: 600;
    color: var(--pp-text);
  }
  .pp-form-header__close {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: var(--pp-radius-sm);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--pp-text-secondary);
    transition: background 0.15s, color 0.15s;
  }
  .pp-form-header__close:hover {
    background: var(--pp-bg-hover);
    color: var(--pp-text);
  }
  .pp-form-header__close:focus-visible {
    outline: 2px solid var(--pp-primary);
    outline-offset: 2px;
  }
  .pp-form-header__close svg {
    width: 18px;
    height: 18px;
  }

  /* ---------- Body ---------- */
  .pp-form-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* ---------- Field group ---------- */
  .pp-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .pp-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--pp-text-secondary);
  }
  .pp-label--required::after {
    content: ' *';
    color: var(--pp-danger);
  }

  /* ---------- Input / Textarea / Select ---------- */
  .pp-input,
  .pp-textarea,
  .pp-select {
    width: 100%;
    padding: 10px 12px;
    font-size: 14px;
    line-height: 1.5;
    color: var(--pp-text);
    background: var(--pp-bg-secondary);
    border: 1px solid var(--pp-border);
    border-radius: var(--pp-radius-sm);
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    font-family: inherit;
  }
  .pp-input::placeholder,
  .pp-textarea::placeholder {
    color: var(--pp-text-placeholder);
  }
  .pp-input:focus,
  .pp-textarea:focus,
  .pp-select:focus {
    border-color: var(--pp-border-focus);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }
  .pp-textarea {
    resize: vertical;
    min-height: 100px;
  }
  .pp-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M2.5 4.5 6 8l3.5-3.5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 32px;
    cursor: pointer;
  }

  /* ---------- File input area ---------- */
  .pp-file-input {
    border: 2px dashed var(--pp-border);
    border-radius: var(--pp-radius-sm);
    padding: 20px 16px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    color: var(--pp-text-secondary);
    font-size: 13px;
    position: relative;
  }
  .pp-file-input:hover,
  .pp-file-input--dragover {
    border-color: var(--pp-primary);
    background: var(--pp-bg-hover);
  }
  .pp-file-input input[type="file"] {
    display: none;
  }
  .pp-file-input__icon {
    display: block;
    margin: 0 auto 8px;
    width: 28px;
    height: 28px;
    color: var(--pp-text-placeholder);
  }
  .pp-file-input__text {
    display: block;
    font-size: 13px;
    color: var(--pp-text-secondary);
  }
  .pp-file-input__hint {
    display: block;
    font-size: 11px;
    color: var(--pp-text-placeholder);
    margin-top: 4px;
  }

  /* ---------- File preview ---------- */
  .pp-file-previews {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }
  .pp-file-preview {
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: var(--pp-radius-sm);
    overflow: hidden;
    border: 1px solid var(--pp-border);
    flex-shrink: 0;
  }
  .pp-file-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .pp-file-preview__remove {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
    padding: 0;
  }
  .pp-file-preview__remove:hover {
    background: var(--pp-danger);
  }

  /* ---------- Badge ---------- */
  .pp-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: var(--pp-primary);
    color: var(--pp-primary-text);
    margin-left: 6px;
  }

  /* ---------- Footer ---------- */
  .pp-form-footer {
    padding: 16px 20px;
    border-top: 1px solid var(--pp-border);
    flex-shrink: 0;
  }

  /* ---------- Submit button ---------- */
  .pp-btn-submit {
    width: 100%;
    padding: 11px 20px;
    font-size: 14px;
    font-weight: 600;
    color: var(--pp-primary-text);
    background: var(--pp-primary);
    border: none;
    border-radius: var(--pp-radius-sm);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background 0.15s, opacity 0.15s;
    font-family: inherit;
    line-height: 1.5;
  }
  .pp-btn-submit:hover:not(:disabled) {
    background: var(--pp-primary-hover);
  }
  .pp-btn-submit:focus-visible {
    outline: 2px solid var(--pp-primary);
    outline-offset: 2px;
  }
  .pp-btn-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .pp-btn-submit__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: pp-spin 0.6s linear infinite;
  }

  /* ---------- Message ---------- */
  .pp-message {
    padding: 12px 16px;
    border-radius: var(--pp-radius-sm);
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    animation: pp-fade-in 0.2s ease;
  }
  .pp-message--success {
    background: var(--pp-success-bg);
    color: var(--pp-success-text);
  }
  .pp-message--error {
    background: var(--pp-error-bg);
    color: var(--pp-error-text);
  }

  /* ---------- Validation error ---------- */
  .pp-field-error {
    font-size: 12px;
    color: var(--pp-danger);
    margin-top: 2px;
  }

  /* ---------- Mobile responsive ---------- */
  @media (max-width: 480px) {
    .pp-form-overlay {
      padding: 0;
      align-items: stretch;
      justify-content: stretch;
    }
    .pp-form-container {
      max-width: 100%;
      max-height: 100dvh;
      border-radius: 0;
    }
    .pp-form-header {
      padding-top: calc(env(safe-area-inset-top, 0px) + 16px);
    }
    .pp-floating-btn {
      bottom: 16px;
    }
    .pp-floating-btn--right {
      right: 16px;
    }
    .pp-floating-btn--left {
      left: 16px;
    }
  }
`;
